import type { LiveValueChip } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useLiveValueChips'
import { LiveValueLabel } from 'features/knowledgeDocumentViewer/components/viewer/shared/LiveValueLabel'
import type { Box } from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import type { ActionMode } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import { shouldBoxBeDisabled } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import React from 'react'

import { LiveValueGlowLayer } from './LiveValueGlowLayer'

type LiveValueChipLayerProps = {
  chips: LiveValueChip[]
  actionMode: ActionMode
  glowPxPerImagePx?: number
  onBoxMouseEnter: (box: Box, event: React.MouseEvent<SVGElement>) => void
  onBoxMouseLeave: () => void
  onBoxClick?: (box: Box, event: React.MouseEvent<SVGElement>) => void
  onBoxAuxClick?: (box: Box, event: React.MouseEvent) => void
  onBoxContextMenu?: (box: Box, event: React.MouseEvent) => void
  onAliasClick?: (highlight: AliasHighlight) => void
  onAliasHover?: (
    highlight: AliasHighlight | null,
    event?: React.MouseEvent
  ) => void
}

type ChipPointerTarget = {
  disabled: boolean
  onMouseEnter: (event: React.MouseEvent<SVGElement>) => void
  onMouseLeave: () => void
  onClick: (event: React.MouseEvent<SVGElement>) => void
  onAuxClick: (event: React.MouseEvent) => void
  onContextMenu: (event: React.MouseEvent) => void
}

const noop = () => undefined

/** Draws readings with a live/stale glow and routes each chip's pointer events to its OCR box or subject. */
export function LiveValueChipLayer({
  chips,
  actionMode,
  glowPxPerImagePx = 1,
  onBoxMouseEnter,
  onBoxMouseLeave,
  onBoxClick,
  onBoxAuxClick,
  onBoxContextMenu,
  onAliasClick,
  onAliasHover,
}: LiveValueChipLayerProps): React.JSX.Element {
  const targetFor = (chip: LiveValueChip): ChipPointerTarget => {
    const { source } = chip.payload

    if (source.kind === 'alias') {
      return {
        disabled: false,
        onMouseEnter: (event) => onAliasHover?.(source.highlight, event),
        onMouseLeave: () => onAliasHover?.(null),
        onClick: () => onAliasClick?.(source.highlight),
        onAuxClick: noop,
        onContextMenu: noop,
      }
    }

    return {
      disabled: shouldBoxBeDisabled(source.box, actionMode),
      onMouseEnter: (event) => onBoxMouseEnter(source.box, event),
      onMouseLeave: onBoxMouseLeave,
      onClick: (event) => onBoxClick?.(source.box, event),
      onAuxClick: (event) => onBoxAuxClick?.(source.box, event),
      onContextMenu: (event) => onBoxContextMenu?.(source.box, event),
    }
  }

  return (
    <>
      <LiveValueGlowLayer
        chips={chips.map((chip) => ({ ...chip, stale: chip.payload.stale }))}
        glowPxPerImagePx={glowPxPerImagePx}
      />
      {chips.map((chip) => {
        const target = targetFor(chip)

        return (
          <g
            key={chip.key}
            style={{
              pointerEvents: target.disabled ? 'none' : 'auto',
              cursor: target.disabled ? 'default' : 'pointer',
            }}
            onMouseEnter={target.onMouseEnter}
            onMouseLeave={target.onMouseLeave}
            onClick={target.onClick}
            onAuxClick={target.onAuxClick}
            onContextMenu={target.onContextMenu}>
            <LiveValueLabel
              x={chip.x}
              y={chip.y}
              width={chip.width}
              height={chip.height}
              text={chip.payload.text}
              stale={chip.payload.stale}
            />
            <rect
              data-live-value-hit
              x={chip.x}
              y={chip.y}
              width={chip.width}
              height={chip.height}
              fill='transparent'
            />
          </g>
        )
      })}
    </>
  )
}
